import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Button, Field, Icon, Select } from '@garnet/ui';
import type { ProviderLink } from '../data/types.js';

/**
 * Sourcing / CRM / GRC connect flow (§7.4 connection lifecycle). One provider's full
 * handshake: credentials → SSO redirect / handshake → success / failure, plus the connected
 * state (linked account · last sync · disconnect with confirm). Faithful port of
 * connect-flow.jsx's SourcingConnectModal: light theme, mono labels, assurance palette.
 */

type Provider = ProviderLink & { category?: string; justConnected?: boolean; account?: string | null };
type Step = 'creds' | 'redirect' | 'handshake' | 'success' | 'failure' | 'connected' | 'confirm';
type CredKind = 'oauth' | 'apikey' | 'generic';
type Category = 'sourcing' | 'crm' | 'grc';

interface CredFieldSpec {
  k: string;
  label: string;
  ph?: string;
  mono?: boolean;
  half?: boolean;
  prefill?: string;
  sel?: string[];
  secret?: boolean;
}

const STALE = 'rotated·8821';

const CONNECT_CSS = `
@keyframes cf-fade{from{opacity:0}to{opacity:1}}
@keyframes cf-rise{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}}
@keyframes cf-pulse{0%,100%{opacity:.3}50%{opacity:1}}
@keyframes cf-spin{to{transform:rotate(360deg)}}
.cf-back{position:fixed;inset:0;z-index:60;background:rgba(26,29,36,.42);
  -webkit-backdrop-filter:blur(2px);backdrop-filter:blur(2px);
  display:flex;align-items:flex-start;justify-content:center;padding:52px 20px 40px;
  overflow-y:auto;animation:cf-fade .14s ease-out}
.cf-modal{width:100%;max-width:544px;background:var(--panel);border:1px solid var(--rule);
  border-radius:var(--radius-lg);box-shadow:0 24px 64px rgba(26,29,36,.24);overflow:hidden;
  animation:cf-rise .18s cubic-bezier(.2,.7,.3,1)}
.cf-head{display:flex;align-items:center;gap:13px;padding:16px 18px;border-bottom:1px solid var(--rule)}
.cf-glyph{font-family:var(--mono);font-size:12.5px;font-weight:700;width:38px;height:38px;flex:none;
  display:flex;align-items:center;justify-content:center;border:1px solid var(--rule);border-radius:7px}
.cf-hname{font-size:14.5px;font-weight:600;line-height:1.2}
.cf-hsub{font-family:var(--mono);font-size:10.5px;color:var(--muted);margin-top:3px}
.cf-x{margin-left:auto;border:1px solid transparent;background:transparent;color:var(--muted);
  width:30px;height:30px;border-radius:var(--radius-md);display:flex;align-items:center;justify-content:center;cursor:pointer}
.cf-x:hover{background:var(--rule-soft);color:var(--ink)}
.cf-body{padding:18px}
.cf-kicker{font-family:var(--mono);font-size:10px;letter-spacing:.14em;text-transform:uppercase;color:var(--muted)}
.cf-title{font-family:var(--display);font-size:19px;font-weight:600;letter-spacing:-.01em;margin:5px 0 0;line-height:1.15}
.cf-lede{font-size:13px;color:var(--muted);line-height:1.5;margin-top:7px}
.cf-foot{display:flex;align-items:center;gap:9px;padding:13px 18px;border-top:1px solid var(--rule);background:#fbfbf9;flex-wrap:wrap}
.cf-foot .spacer{flex:1}
.cf-grid{display:grid;gap:13px;margin-top:16px}
.cf-grid.two{grid-template-columns:1fr 1fr}
@media(max-width:520px){.cf-grid.two{grid-template-columns:1fr}}
.cf-input{width:100%;background:var(--panel);border:1px solid var(--rule);border-radius:var(--radius-md);
  padding:9px 11px;font-size:13px;color:var(--ink)}
.cf-input.mono{font-family:var(--mono);font-size:12.5px}
.cf-input:focus{outline:2px solid var(--verified);outline-offset:1px}
.cf-input::placeholder{color:var(--muted-2)}
.cf-scopes{display:flex;flex-wrap:wrap;gap:6px;margin-top:9px}
.cf-chip{font-family:var(--mono);font-size:10.5px;color:var(--muted);border:1px solid var(--rule);
  border-radius:var(--radius-sm);padding:3px 8px;display:inline-flex;align-items:center;gap:6px;white-space:nowrap}
.cf-chip.on{color:var(--verified-ink);border-color:var(--verified);background:var(--verified-bg)}
.cf-callout{display:flex;gap:10px;border:1px solid var(--rule);border-left:3px solid var(--muted);
  background:var(--rule-soft);padding:11px 13px;border-radius:var(--radius-md);margin-top:14px}
.cf-callout.warn{border-left-color:var(--attest);background:var(--attest-bg)}
.cf-callout .ct{font-size:12.5px;line-height:1.5}
.cf-note{font-family:var(--mono);font-size:11px;color:var(--muted);margin-top:7px}

/* SSO redirect-first */
.cf-session{display:inline-flex;align-items:center;gap:8px;margin-top:15px;font-family:var(--mono);font-size:11.5px;color:var(--ink);
  border:1px solid var(--rule);background:var(--verified-bg);border-color:var(--verified);border-radius:var(--radius-md);padding:6px 11px}
.cf-session .dot{width:7px;height:7px;border-radius:50%;background:var(--verified);flex:none}
.cf-disclose{display:inline-flex;align-items:center;gap:7px;margin-top:16px;background:transparent;border:0;cursor:pointer;
  font-family:var(--mono);font-size:11.5px;color:var(--muted);padding:2px 0;border-bottom:1px dashed var(--rule)}
.cf-disclose:hover{color:var(--ink);border-bottom-color:var(--muted)}
.cf-redirect{text-align:center;padding:30px 18px 26px}
.cf-rbadge{width:54px;height:54px;margin:0 auto;font-family:var(--mono);font-size:17px;font-weight:700;
  display:flex;align-items:center;justify-content:center;border:1px solid var(--rule);border-radius:11px;position:relative}
.cf-rbadge .halo{position:absolute;inset:-7px;border:2px solid var(--rule);border-top-color:var(--verified);
  border-radius:14px;animation:cf-spin .8s linear infinite}
.cf-rstage{font-family:var(--mono);font-size:12px;color:var(--ink);margin-top:16px;min-height:16px}
.cf-rdots{display:inline-flex;gap:5px;justify-content:center;margin-top:13px}
.cf-rdots i{width:6px;height:6px;border-radius:50%;background:var(--rule);display:block}
.cf-rdots i.on{background:var(--verified)}

/* handshake steps */
.cf-hs{border:1px solid var(--rule);border-radius:var(--radius-md);overflow:hidden;margin-top:16px}
.cf-hsrow{display:flex;align-items:center;gap:11px;padding:11px 14px;border-bottom:1px solid var(--rule-soft)}
.cf-hsrow:last-child{border-bottom:0}
.cf-dot{width:18px;height:18px;flex:none;display:flex;align-items:center;justify-content:center}
.cf-dot .ring{width:14px;height:14px;border:2px solid var(--rule);border-top-color:var(--verified);
  border-radius:50%;animation:cf-spin .7s linear infinite}
.cf-dot .idle{width:7px;height:7px;border-radius:50%;background:var(--rule)}
.cf-hslab{font-family:var(--mono);font-size:12px}
.cf-hsrow.pending .cf-hslab{color:var(--muted-2)}
.cf-hsrow.run .cf-hslab{color:var(--ink)}
.cf-hsrow.run{animation:cf-pulse 1s ease-in-out infinite}
.cf-hsrow.done .cf-hslab{color:var(--ink)}
.cf-hsrow.fail .cf-hslab{color:var(--flag-ink)}
.cf-hsmeta{margin-left:auto;font-family:var(--mono);font-size:10.5px;color:var(--muted)}

/* success seal */
.cf-seal{width:46px;height:46px;border-radius:50%;background:var(--verified-bg);
  display:flex;align-items:center;justify-content:center;color:var(--verified);margin-bottom:14px}
.cf-seal.fail{background:var(--flag-bg);color:var(--flag)}

/* account / detail rows */
.cf-detail{border:1px solid var(--rule);border-radius:var(--radius-md);margin-top:16px}
.cf-drow{display:grid;grid-template-columns:128px 1fr;gap:12px;padding:11px 14px;border-bottom:1px solid var(--rule-soft);align-items:baseline}
.cf-drow:last-child{border-bottom:0}
.cf-dk{font-family:var(--mono);font-size:10px;letter-spacing:.06em;text-transform:uppercase;color:var(--muted)}
.cf-dv{font-size:13px}
.cf-dv.mono{font-family:var(--mono);font-size:12.5px}
.cf-danger{background:var(--flag);border-color:var(--flag);color:#fff}
.cf-danger:hover{background:#9c3526}
.cf-link{background:transparent;border:0;color:var(--flag-ink);font-family:var(--body);font-size:13px;
  font-weight:500;cursor:pointer;padding:9px 8px;border-radius:var(--radius-md)}
.cf-link:hover{background:var(--flag-bg)}
`;

/* credential model by auth kind */
function credKind(p: Provider): CredKind {
  if (p.generic) return 'generic';
  if (/API key/i.test(p.auth || '')) return 'apikey';
  return 'oauth';
}

const CRED_SPEC: Record<CredKind, (p: Provider) => CredFieldSpec[]> = {
  oauth: (p) => [
    { k: 'realm', label: p.instLabel, ph: 'AN01000452-PROD-EU', mono: true, half: true, prefill: p.instance !== '—' ? p.instance : '' },
    { k: 'region', label: 'Datacenter', sel: ['EU · Frankfurt', 'UK · London', 'US · Ashburn'], half: true },
    { k: 'clientId', label: 'OAuth client id', ph: 'garnet-svc-prod', mono: true },
    { k: 'secret', label: 'OAuth client secret', ph: 'paste client secret', mono: true, secret: true },
  ],
  apikey: (p) => [
    { k: 'instance', label: 'Instance url', ph: 'lench.coupahost.com', mono: true, prefill: p.instance !== '—' ? p.instance : '' },
    { k: 'apiKey', label: 'API key', ph: 'paste integration key', mono: true, secret: true },
  ],
  generic: () => [
    { k: 'transport', label: 'Transport', sel: ['Webhook (push)', 'SFTP drop', 'CSV upload'] },
    { k: 'endpoint', label: 'Endpoint / host', ph: 'https://hooks.garnetai.net/in/lench', mono: true },
    { k: 'secret', label: 'Shared signing secret', ph: 'signing secret', mono: true, secret: true },
  ],
};
const HS_STEPS: Record<CredKind, string[]> = {
  oauth: ['Resolve realm', 'Exchange OAuth token', 'Verify granted scopes', 'Test pull · supplier master'],
  apikey: ['Reach instance', 'Authenticate API key', 'Verify permissions', 'Test pull · supplier master'],
  generic: ['Validate endpoint', 'Verify signing secret', 'Register webhook', 'Test event · sample payload'],
};
const FAIL_AT: Record<CredKind, number> = { oauth: 1, apikey: 2, generic: 0 };
const REDIRECT_STAGES = ['Opening provider', 'Authorising · existing session', 'Granting read access to Garnet'];
const FAILURE: Record<CredKind, { code: string; what: (n: string) => string; next: (n: string) => string }> = {
  oauth: {
    code: 'authorisation · access_revoked',
    what: (n) => `Garnet's saved authorisation to ${n} no longer works — the grant was revoked or the consent expired.`,
    next: (n) => `Re-authorise from here. You're still signed in to ${n} in this browser, so it's a single click — no keys to copy.`,
  },
  apikey: {
    code: 'authorization · 403 forbidden',
    what: (n) => `The key authenticated, but ${n} returned 403 on the first read.`,
    next: (n) => `Grant the integration key read scope in ${n}, then reconnect.`,
  },
  generic: {
    code: 'endpoint · 502 unreachable',
    what: () => `The endpoint did not return a 2xx to the validation ping within 10s.`,
    next: () => `Confirm the host accepts traffic from Garnet's EU egress range and that the signing secret matches, then retry.`,
  },
};

/* per-category copy — what actually moves across the connection */
const SYNC_COPY: Record<Category, { success: (n: string) => string; stop: (n: string) => string }> = {
  sourcing: {
    success: () => 'Garnet will pull new suppliers and RFx events on the schedule below. The first sync is queued.',
    stop: (n) => `Garnet will stop receiving new suppliers and RFx events from ${n}. Records already pulled are kept and in-flight diligence is unaffected.`,
  },
  crm: {
    success: (n) => `Garnet will sync supplier records and qualification state with ${n} on the schedule below. The first sync is queued.`,
    stop: (n) => `Garnet will stop syncing supplier records and qualification state with ${n}. Records already written are kept.`,
  },
  grc: {
    success: (n) => `Garnet will push verification records, exceptions and evidence to ${n} on the schedule below. The first push is queued.`,
    stop: (n) => `Garnet will stop pushing verification records and exceptions to ${n}. Items already written are kept.`,
  },
};

function accountFor(p: Provider): { user: string; inst: string; region: string } {
  const k = credKind(p);
  if (k === 'apikey') return { user: 'garnet@lench · integration user', inst: p.instance, region: 'EU · Frankfurt' };
  if (k === 'generic') return { user: 'webhook · hooks.garnetai.net/in/lench', inst: p.instance, region: 'EU · Frankfurt' };
  return { user: 'svc.garnet-integration', inst: p.instance, region: 'EU · Frankfurt' };
}

function CredInput({ spec, value, onChange }: { spec: CredFieldSpec; value: string; onChange: (v: string) => void }) {
  if (spec.sel) {
    return <Select value={value} onChange={onChange} ariaLabel={spec.label} options={spec.sel} />;
  }
  return (
    <input
      className={'cf-input' + (spec.mono ? ' mono' : '')}
      type={spec.secret ? 'password' : 'text'}
      placeholder={spec.ph}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      autoComplete="off"
      spellCheck={false}
    />
  );
}

export function ConnectModal({
  provider,
  entry,
  onClose,
  onResolve,
}: {
  provider: Provider;
  entry: string;
  onClose: () => void;
  onResolve: (name: string, patch: Partial<ProviderLink> & Record<string, unknown>) => void;
}) {
  const kind = credKind(provider);
  const specs = CRED_SPEC[kind](provider);
  const [step, setStep] = useState<Step>(entry as Step);
  const [form, setForm] = useState<Record<string, string>>(() => {
    const f: Record<string, string> = {};
    const reconnecting = entry === 'failure' || provider.connection === 'error';
    specs.forEach((s) => {
      f[s.k] = s.sel ? s.sel[0] ?? '' : s.prefill ?? '';
    });
    if (reconnecting && kind === 'oauth' && !f.clientId) f.clientId = 'garnet-svc-prod';
    if (provider.stale && f.secret !== undefined) f.secret = STALE;
    return f;
  });
  const [secretEdited, setSecretEdited] = useState(false);
  const [, setWillFail] = useState(false);
  const [hsi, setHsi] = useState(-1);
  const [hsFailed, setHsFailed] = useState(false);
  const [manual, setManual] = useState(false);
  const [rdi, setRdi] = useState(0);
  const timers = useRef<number[]>([]);

  useEffect(() => {
    if (!document.getElementById('cf-style')) {
      const el = document.createElement('style');
      el.id = 'cf-style';
      el.textContent = CONNECT_CSS;
      document.head.appendChild(el);
    }
  }, []);

  useEffect(() => {
    const captured = timers.current;
    return () => captured.forEach((t) => window.clearTimeout(t));
  }, []);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape' && step !== 'handshake') onClose();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [step, onClose]);

  const steps = HS_STEPS[kind];
  const failIdx = FAIL_AT[kind];

  function runHandshake(fail: boolean) {
    setStep('handshake');
    setHsi(-1);
    setHsFailed(false);
    const stop = fail ? failIdx : steps.length - 1;
    let i = 0;
    const tick = () => {
      setHsi(i);
      if (fail && i === failIdx) {
        const t = window.setTimeout(() => {
          setHsFailed(true);
          const t2 = window.setTimeout(() => setStep('failure'), 620);
          timers.current.push(t2);
        }, 560);
        timers.current.push(t);
        return;
      }
      if (i >= stop) {
        const t = window.setTimeout(() => setStep('success'), 600);
        timers.current.push(t);
        return;
      }
      i += 1;
      const t = window.setTimeout(tick, 560);
      timers.current.push(t);
    };
    const t0 = window.setTimeout(tick, 360);
    timers.current.push(t0);
  }

  function submitCreds() {
    const fail = !!provider.stale && form.secret !== undefined && (!secretEdited || form.secret === STALE);
    setWillFail(fail);
    runHandshake(fail);
  }
  function runRedirect() {
    setStep('redirect');
    setRdi(0);
    const n = REDIRECT_STAGES.length;
    let i = 0;
    const tick = () => {
      i += 1;
      if (i < n) {
        setRdi(i);
        const t = window.setTimeout(tick, 720);
        timers.current.push(t);
      } else {
        const t = window.setTimeout(() => setStep('success'), 720);
        timers.current.push(t);
      }
    };
    const t0 = window.setTimeout(tick, 780);
    timers.current.push(t0);
  }
  function finishConnect() {
    const enteredInst = form.realm || form.instance || form.endpoint || provider.instance;
    onResolve(provider.name, {
      connection: 'connected',
      last: 'just now',
      records: provider.records || 0,
      instance: enteredInst,
      account: accountFor(provider).user,
      justConnected: true,
    });
    setStep('connected');
  }
  function doDisconnect() {
    onResolve(provider.name, { connection: 'disconnected', last: '—', records: 0, account: null });
    onClose();
  }

  const filled = specs.every((s) => String(form[s.k] ?? '').trim().length > 0);
  const acct = accountFor(provider);
  const fcopy = FAILURE[kind];
  const cat: Category = provider.category === 'crm' || provider.category === 'grc' ? provider.category : 'sourcing';
  const copy = SYNC_COPY[cat];

  /* ---- bodies ---- */
  let body: ReactNode = null;
  let foot: ReactNode = null;

  if (step === 'creds') {
    const reconnecting = entry === 'failure' || provider.connection === 'error';
    const canSSO = !provider.generic;
    const showForm = !!provider.generic || manual;
    body = (
      <div className="cf-body">
        <div className="cf-kicker">{provider.generic ? 'Endpoint & secret' : 'Authorise'}</div>
        <div className="cf-title">{reconnecting ? 'Reconnect ' + provider.name : 'Connect ' + provider.name}</div>
        <div className="cf-lede">
          {provider.generic
            ? 'Point a transport at Garnet and share the signing secret used to verify each payload.'
            : manual
              ? 'Use a dedicated service account instead. Paste the credentials from the ' + provider.name + ' developer portal.'
              : 'Garnet opens ' + provider.name + ' to authorise access. You stay in your current session — nothing to copy or paste.'}
        </div>

        {canSSO && !manual && (
          <div className="cf-session">
            <span className="dot" />Signed in to {provider.name} in this browser
          </div>
        )}

        {showForm && (
          <div className="cf-grid two">
            {specs.map((s) => (
              <div key={s.k} style={{ gridColumn: s.half ? 'auto' : '1 / -1' }}>
                <Field label={s.label}>
                  <CredInput
                    spec={s}
                    value={form[s.k] ?? ''}
                    onChange={(v) => {
                      setForm((f) => ({ ...f, [s.k]: v }));
                      if (s.k === 'secret' || s.k === 'apiKey') setSecretEdited(true);
                    }}
                  />
                </Field>
              </div>
            ))}
          </div>
        )}

        <div style={{ marginTop: 16 }}>
          <div className="cf-kicker">Read access requested</div>
          <div className="cf-scopes">
            {provider.flows.map((f) => (
              <span key={f} className="cf-chip">
                <Icon name="check" size={11} />
                {f}
              </span>
            ))}
          </div>
        </div>

        {reconnecting && provider.stale && manual && (
          <div className="cf-callout warn">
            <div style={{ color: 'var(--attest)', paddingTop: 1 }}>
              <Icon name="alert" size={15} />
            </div>
            <div className="ct">The previous secret was rejected. Replace it — leaving the rotated value in place will fail again.</div>
          </div>
        )}

        {canSSO && (
          <button className="cf-disclose" onClick={() => setManual((m) => !m)}>
            <Icon name={manual ? 'arrow' : 'filter'} size={12} />
            {manual ? 'Use single sign-on instead' : 'Enter credentials manually'}
          </button>
        )}
      </div>
    );
    foot = (
      <div className="cf-foot">
        {showForm && <div className="cf-note">credentials stored encrypted · EU residency</div>}
        <div className="spacer" />
        <Button variant="secondary" onClick={onClose}>
          Cancel
        </Button>
        {showForm ? (
          <Button variant="primary" icon="link" disabled={!filled} onClick={submitCreds}>
            Connect
          </Button>
        ) : (
          <Button variant="primary" iconRight="arrow" onClick={runRedirect}>
            Continue with {provider.name}
          </Button>
        )}
      </div>
    );
  } else if (step === 'redirect') {
    body = (
      <div className="cf-body">
        <div className="cf-redirect">
          <div className="cf-rbadge" style={{ borderStyle: provider.generic ? 'dashed' : 'solid' }}>
            <span className="halo" />
            {provider.glyph}
          </div>
          <div className="cf-kicker" style={{ marginTop: 20 }}>
            Authorising
          </div>
          <div className="cf-title" style={{ marginTop: 6 }}>
            Continue in {provider.name}
          </div>
          <div className="cf-rstage">{(REDIRECT_STAGES[rdi] ?? '').replace('provider', provider.name)}…</div>
          <div className="cf-rdots">
            {REDIRECT_STAGES.map((s, i) => (
              <i key={s} className={i <= rdi ? 'on' : ''} />
            ))}
          </div>
        </div>
      </div>
    );
    foot = (
      <div className="cf-foot">
        <div className="cf-note">OAuth 2.0 · authorization code · existing session</div>
      </div>
    );
  } else if (step === 'handshake') {
    body = (
      <div className="cf-body">
        <div className="cf-kicker">Handshake</div>
        <div className="cf-title">Connecting to {provider.name}</div>
        <div className="cf-lede">Verifying credentials and granted scopes. Don't close this window.</div>
        <div className="cf-hs">
          {steps.map((label, i) => {
            const st = i < hsi ? 'done' : i === hsi ? (hsFailed ? 'fail' : 'run') : 'pending';
            return (
              <div key={label} className={'cf-hsrow ' + st}>
                <span className="cf-dot">
                  {st === 'done' && <Icon name="check" size={15} style={{ color: 'var(--verified)' }} />}
                  {st === 'fail' && <Icon name="x" size={15} style={{ color: 'var(--flag)' }} />}
                  {st === 'run' && <span className="ring" />}
                  {st === 'pending' && <span className="idle" />}
                </span>
                <span className="cf-hslab">{label}</span>
                {st === 'done' && <span className="cf-hsmeta">ok</span>}
                {st === 'fail' && (
                  <span className="cf-hsmeta" style={{ color: 'var(--flag-ink)' }}>
                    rejected
                  </span>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
    foot = (
      <div className="cf-foot">
        <div className="cf-note">OAuth 2.0 · token exchange in progress</div>
      </div>
    );
  } else if (step === 'success') {
    body = (
      <div className="cf-body">
        <div className="cf-seal">
          <Icon name="check" size={24} />
        </div>
        <div className="cf-kicker">Connected</div>
        <div className="cf-title">{provider.name} is linked</div>
        <div className="cf-lede">{copy.success(provider.name)}</div>
        <div className="cf-detail">
          <div className="cf-drow">
            <span className="cf-dk">Account</span>
            <span className="cf-dv mono">{acct.user}</span>
          </div>
          <div className="cf-drow">
            <span className="cf-dk">{provider.instLabel}</span>
            <span className="cf-dv mono">{acct.inst}</span>
          </div>
          <div className="cf-drow">
            <span className="cf-dk">Scopes</span>
            <span className="cf-scopes" style={{ marginTop: 0 }}>
              {provider.flows.map((f) => (
                <span key={f} className="cf-chip on">
                  <Icon name="check" size={11} />
                  {f}
                </span>
              ))}
            </span>
          </div>
          <div className="cf-drow">
            <span className="cf-dk">{cat === 'grc' ? 'First push' : 'First sync'}</span>
            <span className="cf-dv">Queued · {provider.flows[0]} in a few minutes</span>
          </div>
        </div>
      </div>
    );
    foot = (
      <div className="cf-foot">
        <div className="spacer" />
        <Button variant="secondary" onClick={finishConnect}>
          Manage connection
        </Button>
        <Button
          variant="primary"
          onClick={() => {
            finishConnect();
            onClose();
          }}
        >
          Done
        </Button>
      </div>
    );
  } else if (step === 'failure') {
    body = (
      <div className="cf-body">
        <div className="cf-seal fail">
          <Icon name="x" size={24} />
        </div>
        <div className="cf-kicker">Connection failed</div>
        <div className="cf-title">Could not connect to {provider.name}</div>
        <div className="cf-note" style={{ color: 'var(--flag-ink)', marginTop: 9 }}>
          {fcopy.code}
        </div>
        <div className="cf-lede" style={{ marginTop: 10, color: 'var(--ink)' }}>
          {fcopy.what(provider.name)}
        </div>
        <div className="cf-callout warn" style={{ marginTop: 14 }}>
          <div style={{ color: 'var(--attest)', paddingTop: 1 }}>
            <Icon name="arrow" size={15} />
          </div>
          <div className="ct">
            <b style={{ fontWeight: 600 }}>Next step.</b> {fcopy.next(provider.name)}
          </div>
        </div>
      </div>
    );
    foot = (
      <div className="cf-foot">
        <div className="spacer" />
        <Button variant="secondary" onClick={onClose}>
          Close
        </Button>
        {provider.generic ? (
          <Button variant="primary" icon="refresh" onClick={() => setStep('creds')}>
            Re-enter credentials
          </Button>
        ) : (
          <Button variant="primary" iconRight="arrow" onClick={runRedirect}>
            Reconnect with {provider.name}
          </Button>
        )}
      </div>
    );
  } else if (step === 'connected') {
    const last = provider.justConnected
      ? 'just now · first sync queued'
      : provider.records
        ? provider.records.toLocaleString() + ' records · ' + provider.last
        : provider.last;
    body = (
      <div className="cf-body">
        <div className="cf-kicker">Connected</div>
        <div className="cf-title">{provider.name}</div>
        <div className="cf-detail">
          <div className="cf-drow">
            <span className="cf-dk">Linked account</span>
            <span className="cf-dv mono" style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
              <span style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--verified)', flex: 'none' }} />
              {acct.user}
            </span>
          </div>
          <div className="cf-drow">
            <span className="cf-dk">{provider.instLabel}</span>
            <span className="cf-dv mono">{acct.inst}</span>
          </div>
          <div className="cf-drow">
            <span className="cf-dk">Residency</span>
            <span className="cf-dv mono">{acct.region}</span>
          </div>
          <div className="cf-drow">
            <span className="cf-dk">Last synced</span>
            <span className="cf-dv">{last}</span>
          </div>
          <div className="cf-drow">
            <span className="cf-dk">Schedule</span>
            <span className="cf-dv">Hourly · incremental · next in ~40 min</span>
          </div>
          <div className="cf-drow">
            <span className="cf-dk">Data flows</span>
            <span className="cf-scopes" style={{ marginTop: 0 }}>
              {provider.flows.map((f) => (
                <span key={f} className="cf-chip on">
                  <Icon name="check" size={11} />
                  {f}
                </span>
              ))}
            </span>
          </div>
        </div>
      </div>
    );
    foot = (
      <div className="cf-foot">
        <button className="cf-link" onClick={() => setStep('confirm')}>
          Disconnect
        </button>
        <div className="spacer" />
        <Button variant="secondary" icon="refresh" onClick={onClose}>
          Sync now
        </Button>
        <Button variant="primary" onClick={onClose}>
          Close
        </Button>
      </div>
    );
  } else if (step === 'confirm') {
    body = (
      <div className="cf-body">
        <div className="cf-seal fail">
          <Icon name="alert" size={22} />
        </div>
        <div className="cf-kicker">Confirm</div>
        <div className="cf-title">Disconnect {provider.name}?</div>
        <div className="cf-lede" style={{ color: 'var(--ink)' }}>
          {copy.stop(provider.name)} The connection can be re-authorised at any time.
        </div>
        <div className="cf-callout" style={{ marginTop: 14 }}>
          <div style={{ color: 'var(--muted)', paddingTop: 1 }}>
            <Icon name="clock" size={15} />
          </div>
          <div className="ct">Scheduled syncs stop immediately. Any sync in progress is allowed to finish.</div>
        </div>
      </div>
    );
    foot = (
      <div className="cf-foot">
        <div className="spacer" />
        <Button variant="secondary" onClick={() => setStep('connected')}>
          Keep connected
        </Button>
        <button className="btn cf-danger" onClick={doDisconnect}>
          <Icon name="x" size={14} />
          Disconnect
        </button>
      </div>
    );
  }

  return (
    <div
      className="cf-back"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && step !== 'handshake') onClose();
      }}
    >
      <div className="cf-modal" role="dialog" aria-modal="true">
        <div className="cf-head">
          <span
            className="cf-glyph"
            style={{ borderStyle: provider.generic ? 'dashed' : 'solid', color: provider.generic ? 'var(--muted)' : 'var(--ink)' }}
          >
            {provider.glyph}
          </span>
          <div>
            <div className="cf-hname">{provider.name}</div>
            <div className="cf-hsub">
              {provider.auth && provider.auth !== '—'
                ? provider.auth
                : kind === 'oauth'
                  ? 'OAuth 2.0'
                  : kind === 'apikey'
                    ? 'API key'
                    : 'generic connector'}
            </div>
          </div>
          <button className="cf-x" onClick={onClose} aria-label="Close" disabled={step === 'handshake'}>
            <Icon name="x" size={16} />
          </button>
        </div>
        {body}
        {foot}
      </div>
    </div>
  );
}
